/**
 * Headshots Service - SportsData.io MLB Headshots API
 * Fetches player headshot URLs, caches by PlayerID, and augments player objects.
 *
 * Routed through the GraphQL Lambda proxy (getHeadshots in
 * fantasyDataService.js) rather than calling api.sportsdata.io directly, so
 * the real SportsData.io key stays server-side (AWS Secrets Manager) instead
 * of shipping in the client bundle.
 *
 * API: https://api.sportsdata.io/v3/mlb/headshots/json/Headshots
 */

import { getHeadshots, FANTASY_DATA_ENABLED } from './fantasyDataService';

let headshotsMap = null;
let fetchPromise = null;

/**
 * Pick best headshot URL from a Headshot API item.
 * Prefer PreferredHostedHeadshotUrl, then no-background, then with-background.
 */
const pickUrl = (item) =>
  item?.PreferredHostedHeadshotUrl ||
  item?.HostedHeadshotNoBackgroundUrl ||
  item?.HostedHeadshotWithBackgroundUrl ||
  null;

/**
 * Fetch Headshots JSON and build Map<playerId, photoUrl>.
 * Caches result in memory; safe to call repeatedly.
 */
export const fetchHeadshotsMap = async () => {
  // FantasyData integration disabled - skip the fetch entirely and return an
  // empty map so players just render their initials-avatar fallback, with no
  // dead API call or console noise. Cached so callers stay cheap.
  if (!FANTASY_DATA_ENABLED) {
    headshotsMap = headshotsMap || new Map();
    return headshotsMap;
  }
  if (headshotsMap) return headshotsMap;
  if (fetchPromise) return fetchPromise;

  fetchPromise = (async () => {
    try {
      const data = await getHeadshots();
      const list = Array.isArray(data) ? data : data?.Headshots ?? data?.headshots ?? [];
      const map = new Map();

      for (const item of list) {
        const pid = item?.PlayerID;
        if (pid == null) continue;
        const urlVal = pickUrl(item);
        if (urlVal) {
          map.set(String(pid), urlVal);
          map.set(Number(pid), urlVal);
        }
      }

      headshotsMap = map;
      return map;
    } catch (e) {
      console.warn('⚠️ Failed to fetch SportsData.io headshots:', e.message);
      fetchPromise = null;
      return new Map();
    }
  })();

  return fetchPromise;
};

/**
 * Get headshot URL for a player ID (number or string).
 * Returns undefined if not found or API not loaded.
 */
export const getHeadshotUrl = async (playerId) => {
  if (playerId == null) return undefined;
  const map = await fetchHeadshotsMap();
  return map.get(String(playerId)) ?? map.get(Number(playerId)) ?? undefined;
};

/**
 * Augment player objects with headshot URLs when missing.
 * Uses id, playerId, or fantasyDataPlayerId for lookup.
 * Mutates players in place; also returns the same array.
 */
export const augmentPlayersWithHeadshots = async (players) => {
  if (!Array.isArray(players) || players.length === 0) return players;
  const map = await fetchHeadshotsMap();
  if (map.size === 0) return players;

  for (const p of players) {
    const haveUrl = p?.photoUrl || p?.headshotUrl || p?.image;
    if (haveUrl) continue;

    const id = p?.fantasyDataPlayerId ?? p?.id ?? p?.playerId;
    if (id == null) continue;

    const url = map.get(String(id)) ?? map.get(Number(id));

    if (url) {
      p.photoUrl = url;
      p.headshotUrl = url;
      p.image = url;
    }
  }

  return players;
};

export default {
  fetchHeadshotsMap,
  getHeadshotUrl,
  augmentPlayersWithHeadshots,
};
